import { Inject, Injectable, InjectionToken } from '@angular/core';
import { defer, EMPTY, forkJoin, from, merge, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, map, switchMap, take, tap } from 'rxjs/operators';
import type { ISaveLabelConfigCommand } from '@protocols/use-cases';

import { LabelConfigState } from '../state/label-config.state';

export const SAVE_LABEL_CONFIG_COMMAND = new InjectionToken<ISaveLabelConfigCommand>('SAVE_LABEL_CONFIG_COMMAND');

/**
 * Persists `LabelConfigState`'s staged rows for a consumer whose ledger exists
 * only for the duration of one submit: the labels page, which has no page-level
 * Save to retry a failed row from, so whatever survives the batch is discarded.
 *
 * Best-effort means per-row: a rejection records itself on the ledger and the
 * batch carries on, so one colour failure cannot take the others down with it.
 *
 * The save-protocol cascade runs the same per-row loop as its own last phase, on
 * its own copy. The duplication is deliberate: the post-batch policies genuinely
 * differ — the cascade leaves failed rows staged for the next page-level Save —
 * and sharing the loop is what previously forced the cascade to inject this class
 * and wait on a public trigger-and-wait observable whose subscribe-before-trigger
 * ordering was a documented footgun. That ordering is now sealed inside this
 * class, with one caller.
 *
 * Public surface is exactly two members: `saving$` and `persistStagedAndDrain`.
 *
 * Not a passthrough — the batch is real work that exists on no state's surface,
 * so the Effects tier is its home.
 */
@Injectable()
export class LabelConfigSaveEffects {
  constructor(
    private readonly labelConfigState: LabelConfigState,
    @Inject(SAVE_LABEL_CONFIG_COMMAND) private readonly saveLabelConfigCommand: ISaveLabelConfigCommand
  ) {}

  // ============================================================================
  // Selectors — passthrough from the ledger
  // ============================================================================

  /**
   * Batch in flight. Openers that trigger the batch themselves gate their
   * trigger on this (the labels page disables its colour action) rather than
   * reaching into the ledger state from a component.
   */
  public readonly saving$ = this.labelConfigState.saving$;

  // ============================================================================
  // One-shot completion signal
  // ============================================================================

  private readonly _completed$ = new Subject<void>();
  /**
   * Emits once per `_persistStaged()` trigger, after the batch settles — whether
   * rows succeeded, failed, or there were none at all. Does not replay, and the
   * zero-row batch emits SYNCHRONOUSLY inside the trigger call, so a caller that
   * waits on this must subscribe BEFORE triggering.
   *
   * Private: the only correct subscribe-before-trigger composition of it is
   * `_persistStagedOnSubscribe$` below, and that has one caller.
   */
  private readonly completed$: Observable<void> = this._completed$.asObservable();

  // ============================================================================
  // Trigger
  // ============================================================================

  /*
   * Best-effort bulk-save of the staged ledger, exactly as staged: rows carry
   * their final `labelUuid` / `methodTypeId` already, because every consolidation
   * flow remaps the shared state eagerly as soon as it learns a real id (a later
   * phase can fail, and the retry pass could never rebuild those maps).
   *
   * Per row: persist, then unstage on success or record the failure keyed by the
   * `(label, method type)` pair on rejection. `saving` brackets the whole batch —
   * the prologue runs in the same turn as the command executions so a UI bound to
   * `saving$` never sees a gap.
   */
  private readonly _persistStaged = this.labelConfigState.effect<void>((trigger$) =>
    trigger$.pipe(
      // `concatMap`, NOT `switchMap`: a trigger arriving mid-batch must not
      // cancel the one in flight — its rows would never unstage and its caller
      // would wait on a `completed$` that can no longer come. Overlapping
      // triggers queue instead, so every trigger still settles exactly once.
      concatMap(() =>
        this.labelConfigState.stagedConfigs$.pipe(
          take(1),
          tap(() => {
            // Clear any failures from a prior attempt so this pass starts clean
            this.labelConfigState.clearSaveExceptions();
            this.labelConfigState.setSaving(true);
          }),
          switchMap((stagedConfigs) => {
            const tasks: Observable<void>[] = stagedConfigs.map((config) =>
              from(
                this.saveLabelConfigCommand.execute({
                  configId: config.originalConfigId,
                  labelUuid: config.labelUuid,
                  methodTypeId: config.methodTypeId,
                  color: config.color,
                })
              ).pipe(
                tap(() => this.labelConfigState.removeConfig(config.stagingId)),
                map(() => undefined),
                catchError((error: Error) => {
                  this.labelConfigState.recordSaveException({
                    labelUuid: config.labelUuid,
                    methodTypeId: config.methodTypeId,
                    error,
                  });
                  return of(undefined);
                })
              )
            );

            // `forkJoin([])` completes without emitting; `defaultIfEmpty` supplies
            // the empty result set so the settle below still runs for zero rows.
            return forkJoin(tasks).pipe(defaultIfEmpty([] as void[]));
          }),
          tap(() => {
            this.labelConfigState.setSaving(false);
            this._completed$.next();
          })
        )
      )
    )
  );

  /**
   * Trigger-and-wait: SUBSCRIBING runs one batch, and the stream emits once —
   * when that batch settles — then completes. Cold, so each subscription is its
   * own batch.
   *
   * The subscribe-before-trigger order is the whole point: `merge` subscribes
   * its sources left-to-right, so `completed$` is live BEFORE `_persistStaged()`
   * fires. Without it the zero-staged-rows path — which settles synchronously
   * inside the trigger — would emit into no subscriber and hang the caller
   * forever.
   *
   * Private, and deliberately so: exposing it made subscribing a side effect that
   * an outside caller could get wrong in exactly that way. Its one caller is
   * `persistStagedAndDrain` below, which composes it correctly.
   */
  private readonly _persistStagedOnSubscribe$: Observable<void> = merge(
    this.completed$.pipe(take(1)),
    defer(() => {
      this._persistStaged();
      return EMPTY;
    })
  );

  /*
   * The TRANSIENT-LEDGER variant: persist the batch, then drop whatever survives
   * it. For consumers whose ledger exists only for the duration of one submit —
   * the labels page, which has no page-level Save to retry from. Draining on
   * every settle means a reopened dialog reads backend truth, and a row that
   * failed never lingers looking like a colour that landed. The recorded
   * exceptions are deliberately left alone: they are what the failure toasts are
   * derived from, and the next batch clears them in its prologue.
   *
   * Assumes this batch is its consumer's ONLY save story. `completed$` is one
   * shared channel, so a consumer that also drove the cascade could have a
   * cascade-settle satisfy a pending drain. The cascade no longer routes through
   * this class, so nothing else can emit on that channel; correlating completions
   * per trigger is the fix if a consumer ever adds a second trigger.
   */
  public readonly persistStagedAndDrain = this.labelConfigState.effect<void>((trigger$) =>
    trigger$.pipe(
      // `concatMap` for the same reason the batch uses it: overlapping triggers
      // queue instead of cancelling, so every trigger drains exactly once.
      concatMap(() => this._persistStagedOnSubscribe$.pipe(tap(() => this.labelConfigState.clearStaged())))
    )
  );
}
